<template>
  <div class="dash-wrap">
    <!-- Plan Expiry Banner -->
    <v-row v-if="showDate" class="mb-4 mx-0">
      <v-col cols="12" class="pa-0">
        <div
          style="
            background: linear-gradient(135deg, #f59e0b, #f97316);
            border-radius: 12px;
            padding: 14px 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          "
        >
          <div style="display: flex; align-items: center; gap: 10px">
            <v-icon style="color: #fff">mdi-clock-alert-outline</v-icon>
            <span style="color: #fff; font-size: 13.5px; font-weight: 600">
              Your current plan expires on
              <strong>{{
                getFormattedDate(currentPlan?.expiration_date)
              }}</strong>
            </span>
          </div>
          <router-link to="/societyadmin/plan" tag="span">
            <v-btn
              small
              rounded
              dark
              style="
                background: #fff;
                color: #f59e0b;
                font-weight: 700;
                font-size: 12px;
                text-transform: none;
              "
              >Buy Plan</v-btn
            >
          </router-link>
        </div>
      </v-col>
    </v-row>

    <!-- ───────────────────────────────────────────
         SETUP CHECKLIST PANEL (collapsible)
    ─────────────────────────────────────────────── -->
    <v-row v-if="showSetupBanner" class="mb-4 mx-0">
      <v-col cols="12" class="pa-0">
        <div class="sc-panel">
          <!-- ── COLLAPSED BAR (always visible) ── -->
          <div class="sc-bar" @click="scExpanded = !scExpanded">
            <!-- left: icon + title -->
            <div class="sc-bar-left">
              <div class="sc-bar-icon" :style="{ background: brandColor }">
                <v-icon size="18" color="#fff"
                  >mdi-rocket-launch-outline</v-icon
                >
              </div>
              <div class="sc-bar-info">
                <span class="sc-bar-title">Setup Checklist</span>
                <span class="sc-bar-sub"
                  >{{ setupCompletedSteps }} of {{ setupTotalSteps }} steps
                  completed</span
                >
              </div>
            </div>

            <!-- center: progress bar -->
            <div class="sc-bar-progress">
              <div class="sc-bar-track">
                <div
                  class="sc-bar-fill"
                  :style="{
                    width: setupProgressPct + '%',
                    background: brandColor,
                  }"
                ></div>
              </div>
              <span class="sc-bar-pct" :style="{ color: brandColor }"
                >{{ setupProgressPct }}%</span
              >
            </div>

            <!-- right: expand toggle + dismiss -->
            <div class="sc-bar-actions">
              <v-btn
                icon
                x-small
                class="sc-toggle-btn"
                :style="scExpanded ? { transform: 'rotate(180deg)' } : {}"
              >
                <v-icon size="18" color="var(--tx3)">mdi-chevron-down</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                class="sc-close-btn"
                @click.stop="dismissSetupBanner"
              >
                <v-icon size="16" color="var(--tx3)">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- ── EXPANDED STEPS LIST ── -->
          <transition name="sc-expand">
            <div v-if="scExpanded" class="sc-body">
              <div class="sc-steps-grid">
                <div
                  v-for="(step, idx) in setupStatus.steps"
                  :key="step.key"
                  class="sc-step"
                  :class="{
                    'sc-step-done': step.done,
                    'sc-step-pending': !step.done,
                  }"
                >
                  <!-- Status indicator -->
                  <div
                    class="sc-step-icon"
                    :style="
                      step.done
                        ? { background: brandColor, borderColor: brandColor }
                        : {
                            background: 'var(--surface)',
                            borderColor: 'var(--border)',
                          }
                    "
                  >
                    <v-icon v-if="step.done" size="14" color="#fff"
                      >mdi-check</v-icon
                    >
                    <span
                      v-else
                      class="sc-step-num"
                      :style="{ color: 'var(--tx3)' }"
                      >{{ idx + 1 }}</span
                    >
                  </div>

                  <!-- Step info -->
                  <div class="sc-step-body">
                    <div class="sc-step-label">
                      <v-icon
                        size="13"
                        :color="step.done ? brandColor : 'var(--tx3)'"
                        style="margin-right: 4px"
                        >{{ stepIcon(step.key) }}</v-icon
                      >
                      {{ step.label }}
                      <span v-if="step.done" class="sc-badge sc-badge-done"
                        >Done</span
                      >
                      <span
                        v-else-if="step.key === 'requests' && step.count > 0"
                        class="sc-badge sc-badge-warn"
                        >{{ step.count }} pending</span
                      >
                      <span v-else class="sc-badge sc-badge-todo">To Do</span>
                    </div>
                    <div class="sc-step-desc">{{ stepDesc(step.key) }}</div>
                  </div>

                  <!-- Quick action button (only for pending) -->
                  <router-link
                    v-if="!step.done"
                    :to="step.route"
                    class="text-decoration-none"
                    style="flex-shrink: 0"
                  >
                    <v-btn
                      x-small
                      depressed
                      class="sc-action-btn"
                      :style="{
                        background: 'rgba(' + brandRgb + ',0.10)',
                        color: brandColor,
                      }"
                    >
                      {{ stepActionLabel(step.key) }}
                      <v-icon right size="11">mdi-arrow-right</v-icon>
                    </v-btn>
                  </router-link>
                  <div v-else class="sc-check-done">
                    <v-icon size="16" :color="brandColor"
                      >mdi-check-circle</v-icon
                    >
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="sc-footer">
                <span class="sc-footer-note"
                  >Complete all steps to make the most of iSmartSociety</span
                >
                <v-btn
                  text
                  x-small
                  class="sc-skip-btn"
                  @click="dismissSetupBanner"
                  >Dismiss</v-btn
                >
              </div>
            </div>
          </transition>
        </div>
      </v-col>
    </v-row>

    <!-- Society Code Row -->
    <v-row class="mb-3 mx-0 align-center">
      <v-col cols="12" class="pa-0">
        <div class="dash-toprow">
          <div class="pt-title">Dashboard Overview</div>
          <div class="society-code-wrap">
            <span class="society-code-label">Society Code:</span>
            <span class="society-code-val">{{ societyCode }}</span>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Stats -->
    <v-row class="stats-row mb-3 mx-0">
      <v-col cols="6" sm="6" md="3" lg="3" xl="3" class="stats-col pa-2">
        <PremiumStatCard
          label="Total Members"
          :value="members"
          icon="mdi-account-group-outline"
          color="#10B981"
          :loading="!loaded"
          to="/societyadmin/member/list"
        />
      </v-col>
      <v-col cols="6" sm="6" md="3" lg="3" xl="3" class="stats-col pa-2">
        <PremiumStatCard
          label="Monthly Collection"
          :value="monthly_collection || '₹0.0L'"
          icon="mdi-receipt-outline"
          color="#F59E0B"
          :loading="!loaded"
          to="/societyadmin/bills/house"
        />
      </v-col>
      <v-col cols="6" sm="6" md="3" lg="3" xl="3" class="stats-col pa-2">
        <PremiumStatCard
          label="Open Tickets"
          :value="openComplaints"
          icon="mdi-headset"
          color="#EF4444"
          :loading="!loaded"
          to="/societyadmin/complaints"
        />
      </v-col>
      <v-col cols="6" sm="6" md="3" lg="3" xl="3" class="stats-col pa-2">
        <PremiumStatCard
          label="Visitors Today"
          :value="visitors_today"
          icon="mdi-walk"
          color="#3B82F6"
          :loading="!loaded"
          to="/societyadmin/visitor"
        />
      </v-col>
      <v-col cols="6" sm="6" md="3" lg="3" xl="3" class="stats-col pa-2">
        <PremiumStatCard
          label="Total Vehicles"
          :value="vehicles"
          icon="mdi-car-outline"
          color="#14B8A6"
          :loading="!loaded"
          to="/societyadmin/vehicle"
        />
      </v-col>
    </v-row>
    

    <!-- Finance Chart -->
    <v-row class="mb-5 mx-0">
      <v-col cols="12" sm="12" md="8" lg="9" class="pa-2">
        <div class="panel">
          <div class="panel-hdr">
            <div>
              <div class="panel-title">
                Bills vs Expenses — Monthly Overview
              </div>
              <div class="panel-sub">Last 6 months · Nov 2025 – Apr 2026</div>
            </div>
            <div style="display: flex; align-items: center; gap: 16px">
              <div style="display: flex; gap: 14px">
                <span
                  style="
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 11px;
                    color: var(--tx3);
                  "
                  ><span
                    style="
                      width: 10px;
                      height: 10px;
                      border-radius: 2px;
                      background: #10b981;
                    "
                  ></span
                  >Bills</span
                >
                <span
                  style="
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 11px;
                    color: var(--tx3);
                  "
                  ><span
                    style="
                      width: 10px;
                      height: 10px;
                      border-radius: 2px;
                      background: #f59e0b;
                    "
                  ></span
                  >Expenses</span
                >
              </div>
              <router-link
                to="/societyadmin/finance/report"
                class="view-all text-decoration-none"
                >Full Report →</router-link
              >
            </div>
          </div>
          <div class="panel-body chart-body">
            <div class="chart-container-scroll">
              <div class="chart-inner-wrap">
                <bar-chart
                  v-if="finChartData"
                  :chart-data="finChartData"
                  :options="finChartOptions"
                  style="height: 100%; width: 100%"
                />
                <v-progress-circular
                  v-else-if="finLoading"
                  indeterminate
                  color="var(--brand)"
                  size="24"
                ></v-progress-circular>
                <div
                  v-else-if="loaded"
                  style="text-align: center; color: var(--tx4)"
                >
                  <v-icon large class="mb-2" style="color: var(--border)"
                    >mdi-chart-bar-stacked</v-icon
                  >
                  <div style="font-size: 13px; font-weight: 500">
                    No financial data found for this period
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-col>
           <!-- Visitor Self Check-in -->
      <v-col cols="12" sm="12" md="4" lg="3" class="pa-2">
        <div class="panel">
          <div class="panel-hdr">
            <div>
              <div class="panel-title">Visitor Self Check-in</div>
              <div class="panel-sub" style="font-size:11.5px;color:var(--tx3);margin-top:2px;">
                Print this QR code and place it at the entrance
              </div>
            </div>
            <div style="display:flex;align-items:center;gap:10px;">
              <router-link to="/societyadmin/visitor" class="view-all text-decoration-none">View Visitors →</router-link>
              <!-- <v-btn
                small depressed dark
                :color="brandColor"
                style="border-radius:8px;text-transform:none;font-weight:600;font-size:12px;"
                :loading="qrLoading"
                @click="loadQR"
                v-if="!qrImageUrl"
              >
                <v-icon left size="15">mdi-qrcode</v-icon>
                Generate QR
              </v-btn> -->
            </div>
          </div>

          <div class="panel-body">
            <!-- Loading -->
            <!-- <div v-if="qrLoading" style="display:flex;flex-direction:column;align-items:center;padding:40px 0;">
              <v-progress-circular indeterminate :color="brandColor" size="42" width="3" />
              <p style="font-size:12px;color:var(--tx3);margin-top:14px;">Generating QR Code &amp; PDF...</p>
            </div> -->

            <!-- Content -->
            <div class="vqr-inline-grid">

              <!-- Steps -->
              <div class="vqr-inline-steps">
                <p style="font-size:13px;color:var(--tx3);line-height:1.6;margin-bottom:20px;">
                  Generate a QR code for visitors to self check-in at the gate without staff assistance.
                </p>
                <div class="vqr-inline-step">
                  <span class="vqr-inline-step-num" style="background:#EFF6FF;color:#3B82F6;">1</span>
                  <div>
                    <div class="vqr-inline-step-title">Download the PDF</div>
                    <!-- <div class="vqr-inline-step-sub">Print at 3×3 inches or larger</div> -->
                  </div>
                </div>
                <div class="vqr-inline-step">
                  <span class="vqr-inline-step-num" style="background:#FFFBEB;color:#F59E0B;">2</span>
                  <div>
                    <div class="vqr-inline-step-title">Place it at the gate</div>
                    <!-- <div class="vqr-inline-step-sub">Laminate for outdoor durability</div> -->
                  </div>
                </div>
                <div class="vqr-inline-step">
                  <span class="vqr-inline-step-num" style="background:#ECFDF5;color:#10B981;">3</span>
                  <div>
                    <div class="vqr-inline-step-title">Visitors scan to enter</div>
                    <!-- <div class="vqr-inline-step-sub">No app download needed</div> -->
                  </div>
                </div>

                <div v-if="pdfUrl" style="margin-top:20px;">
                  <v-btn
                    depressed dark
                    :color="brandColor"
                    style="border-radius:8px;text-transform:none;font-weight:600;"
                    @click="downloadPDF"
                  >
                    <v-icon left size="16">mdi-download</v-icon>
                    Download PDF
                  </v-btn>
                </div>
              </div>

            

            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Row 2: Quick Actions, Recent Expenses, Collection Health -->
    <v-row class="mb-5 mx-0">
      <!-- Quick Actions -->
      <v-col cols="12" sm="6" md="6" lg="4" class="pa-2">
        <div class="panel h-100">
          <div class="panel-hdr">
            <div class="panel-title">Quick Actions</div>
          </div>
          <div class="panel-body">
            <div class="q-grid">
              <router-link
                to="/societyadmin/member/add"
                class="text-decoration-none"
              >
                <div class="q-btn">
                  <div class="q-icon" style="background: #eff6ff">
                    <v-icon style="color: #3b82f6; font-size: 20px"
                      >mdi-account-plus-outline</v-icon
                    >
                  </div>
                  <div class="q-lbl">Add Member</div>
                </div>
              </router-link>
              <router-link
                to="/societyadmin/committemember?add=1"
                class="text-decoration-none"
              >
                <div class="q-btn">
                  <div class="q-icon" style="background: #f5f3ff">
                    <v-icon style="color: #8b5cf6; font-size: 20px"
                      >mdi-account-group-outline</v-icon
                    >
                  </div>
                  <div class="q-lbl">Add Committee</div>
                </div>
              </router-link>
              <router-link
                to="/societyadmin/notice/add"
                class="text-decoration-none"
              >
                <div class="q-btn">
                  <div class="q-icon" style="background: #fffbeb">
                    <v-icon style="color: #f59e0b; font-size: 20px"
                      >mdi-bell-outline</v-icon
                    >
                  </div>
                  <div class="q-lbl">Add Notice</div>
                </div>
              </router-link>
              <router-link
                to="/societyadmin/event/add"
                class="text-decoration-none"
              >
                <div class="q-btn">
                  <div class="q-icon" style="background: #f0fdf4">
                    <v-icon style="color: #16a34a; font-size: 20px"
                      >mdi-calendar-plus</v-icon
                    >
                  </div>
                  <div class="q-lbl">Add Event</div>
                </div>
              </router-link>
              <router-link
                to="/societyadmin/bills/add"
                class="text-decoration-none"
              >
                <div class="q-btn">
                  <div class="q-icon" style="background: #ecfdf5">
                    <v-icon style="color: #10b981; font-size: 20px"
                      >mdi-receipt-text-outline</v-icon
                    >
                  </div>
                  <div class="q-lbl">Add Bill</div>
                </div>
              </router-link>
              <router-link
                to="/societyadmin/expense/add"
                class="text-decoration-none"
              >
                <div class="q-btn">
                  <div class="q-icon" style="background: #fff7ed">
                    <v-icon style="color: #f97316; font-size: 20px"
                      >mdi-wallet-plus-outline</v-icon
                    >
                  </div>
                  <div class="q-lbl">Add Expense</div>
                </div>
              </router-link>
              <router-link
                to="/societyadmin/update?add=1"
                class="text-decoration-none"
              >
                <div class="q-btn">
                  <div class="q-icon" style="background: #fef2f2">
                    <v-icon style="color: #ef4444; font-size: 20px"
                      >mdi-bullhorn-outline</v-icon
                    >
                  </div>
                  <div class="q-lbl">Add Announce</div>
                </div>
              </router-link>
              <router-link
                to="/societyadmin/staff?add=1"
                class="text-decoration-none"
              >
                <div class="q-btn">
                  <div class="q-icon" style="background: #f3f4f6">
                    <v-icon style="color: #4b5563; font-size: 20px"
                      >mdi-briefcase-account-outline</v-icon
                    >
                  </div>
                  <div class="q-lbl">Add Staff</div>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Recent Expenses -->
      <v-col cols="12" sm="6" md="6" lg="4" class="pa-2">
        <div class="panel h-100">
          <div class="panel-hdr">
            <div class="panel-title">Recent Expenses</div>
            <router-link
              to="/societyadmin/expense"
              class="view-all text-decoration-none"
              >All →</router-link
            >
          </div>
          <div class="panel-body">
            <div
              v-if="latestExpenses.length === 0"
              style="
                padding: 20px;
                text-align: center;
                color: var(--tx4);
                font-size: 13px;
              "
            >
              No recent expenses
            </div>
            <div v-for="exp in latestExpenses" :key="exp.id" class="mod-row">
              <div class="mod-l">
                <span class="dot dr"></span>
                <div>
                  <v-menu
                    open-on-hover
                    bottom
                    offset-y
                    :close-delay="300"
                    content-class="premium-menu-tooltip"
                    :disabled="!exp.isTruncated"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        v-bind="attrs"
                        v-on="on"
                        class="expense-title-trim"
                        @mouseenter="checkTruncation($event, exp)"
                      >
                        {{ exp.title }}
                      </div>
                    </template>
                    <div class="premium-tooltip-card">
                      <div class="premium-tooltip-scroll">{{ exp.title }}</div>
                    </div>
                  </v-menu>
                  <div style="font-size: 10px; color: var(--tx3)">
                    {{ exp.category ? exp.category.name : "Other" }} ·
                    {{ formatDate(exp.date, "MMM D") }}
                  </div>
                </div>
              </div>
              <div class="mod-v" style="color: var(--red)">
                -₹{{ Number(exp.amount).toLocaleString() }}
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Collection Health -->
      <v-col cols="12" sm="6" md="6" lg="4" class="pa-2">
        <div class="panel h-100">
          <div class="panel-hdr">
            <div class="panel-title">Collection Health</div>
            <div style="font-size: 11px; color: var(--tx4); font-weight: 500">
              Apr 2026
            </div>
          </div>
          <div class="panel-body">
            <div class="hbar">
              <div class="hbar-meta">
                <span class="hbar-lbl">Collected</span
                ><span class="hbar-pct" style="color: #10b981">{{
                  loaded ? colHealth.pct + "%" : "..."
                }}</span>
              </div>
              <div class="hbar-track">
                <div
                  class="hbar-fill"
                  :style="`width:${
                    loaded ? colHealth.pct + '%' : '0%'
                  };background:#10B981`"
                ></div>
              </div>
            </div>
            <div class="hbar">
              <div class="hbar-meta">
                <span class="hbar-lbl">Pending</span
                ><span class="hbar-pct" style="color: var(--amber)">{{
                  loaded ? 100 - colHealth.pct + "%" : "..."
                }}</span>
              </div>
              <div class="hbar-track">
                <div
                  class="hbar-fill"
                  :style="`width:${
                    loaded ? 100 - colHealth.pct + '%' : '0%'
                  };background:var(--amber)`"
                ></div>
              </div>
            </div>
            <div class="hbar">
              <div class="hbar-meta">
                <span class="hbar-lbl">Overdue</span
                ><span class="hbar-pct" style="color: var(--red)">{{
                  loaded ? colHealth.overdue_pct + "%" : "..."
                }}</span>
              </div>
              <div class="hbar-track">
                <div
                  class="hbar-fill"
                  :style="`width:${
                    loaded ? colHealth.overdue_pct + '%' : '0%'
                  };background:var(--red)`"
                ></div>
              </div>
            </div>
            <div
              style="
                margin-top: 20px;
                padding: 14px;
                background: rgba(16, 185, 129, 0.05);
                border-radius: 10px;
                display: flex;
                align-items: center;
                gap: 12px;
              "
            >
              <div
                style="
                  width: 36px;
                  height: 36px;
                  border-radius: 50%;
                  background: #10b98122;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: #10b981;
                "
              >
                <v-icon small color="#10B981">mdi-currency-inr</v-icon>
              </div>
              <div>
                <div
                  style="
                    font-size: 11px;
                    color: var(--tx4);
                    font-weight: 600;
                    margin-bottom: 2px;
                  "
                >
                  Truly Collected
                </div>
                <div
                  style="font-size: 14px; font-weight: 800; color: var(--tx)"
                >
                  ₹{{
                    loaded
                      ? Number(colHealth.collected).toLocaleString()
                      : "..."
                  }}
                </div>
              </div>
            </div>
            <div
              style="
                margin-top: 12px;
                padding: 14px;
                background: rgba(239, 68, 68, 0.05);
                border-radius: 10px;
                display: flex;
                align-items: center;
                gap: 12px;
              "
            >
              <div
                style="
                  width: 36px;
                  height: 36px;
                  border-radius: 50%;
                  background: #ef444422;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: #ef4444;
                "
              >
                <v-icon small color="#EF4444">mdi-alert-circle-outline</v-icon>
              </div>
              <div>
                <div
                  style="
                    font-size: 11px;
                    color: var(--tx4);
                    font-weight: 600;
                    margin-bottom: 2px;
                  "
                >
                  Overdue Amount
                </div>
                <div
                  style="font-size: 14px; font-weight: 800; color: var(--tx)"
                >
                  ₹{{
                    loaded ? Number(colHealth.overdue).toLocaleString() : "..."
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Row 3: Growth and Occupancy -->
    <v-row class="mb-5 mx-0">
      <!-- Growth Chart -->
      <v-col cols="12" lg="8" md="12" class="pa-2">
        <div class="panel h-100">
          <div class="panel-hdr">
            <div class="panel-title">Society Growth & Visitor Trend</div>
          </div>
          <div class="panel-body chart-body">
            <div class="chart-container-scroll">
              <div class="chart-inner-wrap">
                <line-chart
                  v-if="loaded"
                  :chart-data="growChartData"
                  :options="growChartOptions"
                  style="height: 100%; width: 100%"
                />
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Occupancy -->
      <v-col cols="12" lg="4" md="12" class="pa-2">
        <div class="panel h-100">
          <div class="panel-hdr">
            <div class="panel-title">Occupancy</div>
          </div>
          <div class="panel-body text-center">
            <div
              style="
                display: flex;
                justify-content: center;
                margin-bottom: 16px;
              "
            >
              <div style="position: relative; width: 96px; height: 96px">
                <pie-chart
                  v-if="loaded && occupancy.total > 0"
                  :chart-data="occChartData"
                  :options="occChartOptions"
                  style="width: 100%; height: 100%"
                />
                <div
                  style="
                    position: absolute;
                    inset: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    pointer-events: none;
                  "
                >
                  <div
                    style="
                      font-size: 20px;
                      font-weight: 800;
                      color: var(--tx);
                      letter-spacing: -0.04em;
                    "
                  >
                    {{ loaded ? occupancy.pct + "%" : "..." }}
                  </div>
                  <div
                    style="font-size: 9px; color: var(--tx4); font-weight: 500"
                  >
                    Occupied
                  </div>
                </div>
              </div>
            </div>
            <div class="mod-row">
              <div class="mod-l">
                <span class="dot dg" style="background: #10b981"></span>Occupied
                Flats
              </div>
              <div class="mod-v">
                {{ loaded ? `${occupancy.occupied} units` : "..." }}
              </div>
            </div>
            <div class="mod-row">
              <div class="mod-l">
                <span class="dot da" style="background: #f59e0b"></span>Owners
              </div>
              <div class="mod-v">{{ loaded ? occupancy.owners : "..." }}</div>
            </div>
            <div class="mod-row">
              <div class="mod-l">
                <span class="dot da" style="background: #3b82f6"></span>Tenants
              </div>
              <div class="mod-v">{{ loaded ? occupancy.tenants : "..." }}</div>
            </div>
            <div class="mod-row">
              <div class="mod-l">
                <span class="dot dr" style="background: #e5e7eb"></span>Vacant
              </div>
              <div class="mod-v">{{ loaded ? occupancy.vacant : "..." }}</div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Row 4: Events, Requests, Tickets -->
    <v-row class="mb-5 mx-0">
   

      <!-- Upcoming Events -->
      <v-col cols="12" sm="6" md="6" lg="4" class="pa-2">
        <div class="panel h-100">
          <div class="panel-hdr">
            <div class="panel-title">Upcoming Events</div>
            <router-link
              to="/societyadmin/event"
              class="view-all text-decoration-none"
              >All →</router-link
            >
          </div>
          <div class="panel-body">
            <div
              v-if="latestEvents.length === 0"
              style="
                padding: 20px;
                text-align: center;
                color: var(--tx4);
                font-size: 13px;
              "
            >
              No upcoming events
            </div>
            <div
              v-for="(ev, idx) in latestEvents"
              :key="ev.id"
              class="mini-ev"
              :style="`background:rgba(${
                idx % 3 === 0
                  ? '16,185,129'
                  : idx % 3 === 1
                  ? '245,158,11'
                  : '59,130,246'
              },.08)`"
            >
              <div
                class="mini-ev-date"
                :style="`background:${
                  idx % 3 === 0
                    ? '#10B981'
                    : idx % 3 === 1
                    ? '#F59E0B'
                    : '#3B82F6'
                }`"
              >
                <div class="mini-ev-mo">
                  {{ formatDate(ev.start_datetime, "MMM") }}
                </div>
                <div class="mini-ev-day">
                  {{ formatDate(ev.start_datetime, "DD") }}
                </div>
              </div>
              <div>
                <div class="mini-ev-name">{{ ev.title }}</div>
                <div class="mini-ev-loc">
                  {{ formatDate(ev.start_datetime, "h A") }} ·
                  {{ ev.description.substring(0, 30) }}...
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Member Requests -->
      <v-col cols="12" sm="6" md="6" lg="4" class="pa-2">
        <div class="panel h-100">
          <div class="panel-hdr">
            <div class="panel-title">Member Requests</div>
            <span class="tag t-amber">{{ memberRequests }} pending</span>
          </div>
          <div class="panel-body">
            <div
              v-if="latestRequests.length === 0"
              style="
                padding: 15px;
                text-align: center;
                color: var(--tx4);
                font-size: 12px;
              "
            >
              No pending requests
            </div>
            <div v-for="req in latestRequests" :key="req.id" class="mod-row">
              <div class="mod-l">
                <span class="dot da"></span>
                <span class=""
                  >{{
                    req.name && req.name !== "null" ? req.name : "—"
                  }}
                  -</span
                >
                <div class="house-number-badge">
                  <v-icon size="14" color="#94A38B">mdi-home-outline</v-icon>
                  <span class="flat-prefix">{{ req.flat_name }}-</span>
                  <span class="house-num">{{ req.house_number || "—" }}</span>
                </div>
              </div>
              <router-link
                class="mod-v text-decoration-none"
                to="/societyadmin/member/requests"
                style="color: var(--brand); font-size: 12px; font-weight: 600"
                >Review →</router-link
              >
            </div>
          </div>
        </div>
      </v-col>

 
    </v-row>
    <!-- Row 5: Help Desk & Notices -->
    <v-row class="mb-5 mx-0">
        <!-- Recent Tickets -->
      <v-col cols="12" sm="6" md="6" lg="4" class="pa-2">
        <div class="panel h-100">
          <div class="panel-hdr">
            <div class="panel-title">Recent Tickets</div>
            <router-link
              to="/societyadmin/complaints"
              class="view-all text-decoration-none"
              >All →</router-link
            >
          </div>
          <div class="panel-body">
            <div
              v-if="latestTickets.length === 0"
              style="
                padding: 20px;
                text-align: center;
                color: var(--tx4);
                font-size: 13px;
              "
            >
              No recent tickets
            </div>
            <div v-for="t in latestTickets" :key="t.id" class="mod-row">
              <div class="mod-l">
                <span
                  class="dot"
                  :style="`background:${getStatusColor(t.status)}`"
                ></span>
                <div class="ticket-title-trim">{{ t.title }}</div>
              </div>
              <div class="mod-v" :style="`color:${getStatusColor(t.status)}`">
                {{ t.status_name }}
              </div>
            </div>
          </div>
        </div>
      </v-col>
      <!-- Help Desk Issues -->
      <v-col cols="12" sm="6" md="6" lg="4" class="pa-2">
        <div class="panel h-100">
          <div class="panel-hdr">
            <div class="panel-title">Help Desk Issues</div>
            <span class="tag t-red">{{ openComplaints }} open</span>
          </div>
          <div class="panel-body">
            <div
              v-if="latestComplaints.length === 0"
              style="
                padding: 15px;
                text-align: center;
                color: var(--tx4);
                font-size: 12px;
              "
            >
              No open issues
            </div>
            <div
              v-for="comp in latestComplaints"
              :key="comp.id"
              class="mod-row"
            >
              <div class="mod-l">
                <span class="dot dr"></span>{{ comp.title }} —
                <span
                  v-if="comp.priority == 0"
                  style="color: var(--red); font-weight: 600"
                  >Emergency</span
                >
                <span v-else-if="comp.priority == 1">Low</span>
                <span v-else-if="comp.priority == 2">Medium</span>
                <span
                  v-else-if="comp.priority == 3"
                  style="color: var(--orange); font-weight: 600"
                  >High</span
                >
                <span v-else>General</span>
              </div>
              <router-link
                :to="`/societyadmin/complaints/view/${comp.id}`"
                class="mod-v text-decoration-none"
                :style="`color:${
                  comp.status === 1 ? 'var(--amber)' : 'var(--red)'
                };font-size:12px;font-weight:600;`"
              >
                {{ comp.status === 1 ? "In Progress" : "Open" }} →
              </router-link>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Latest Notices -->
      <v-col cols="12" sm="6" md="6" lg="4" class="pa-2">
        <div class="panel h-100">
          <div class="panel-hdr">
            <div class="panel-title">Latest Notices</div>
            <span class="tag t-green">{{ latestNotices.length }} active</span>
          </div>
          <div class="panel-body">
            <div
              v-if="latestNotices.length === 0"
              style="
                padding: 15px;
                text-align: center;
                color: var(--tx4);
                font-size: 12px;
              "
            >
              No recent notices
            </div>
            <div
              v-for="notice in latestNotices"
              :key="notice.id"
              class="mod-row"
            >
              <div class="mod-l">
                <span class="dot dg"></span>
                <v-menu
                  open-on-hover
                  bottom
                  offset-y
                  :close-delay="300"
                  content-class="premium-menu-tooltip"
                  :disabled="!notice.isTruncated"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      v-on="on"
                      class="expense-title-trim"
                      style="max-width: 360px"
                      @mouseenter="checkTruncation($event, notice)"
                    >
                      {{ notice.title }}
                    </div>
                  </template>
                  <div class="premium-tooltip-card">
                    <div class="premium-tooltip-scroll">{{ notice.title }}</div>
                  </div>
                </v-menu>
              </div>
              <div class="mod-v" style="color: var(--tx4); font-size: 12px">
                {{ formatDate(notice.created_at, "MMM D") }}
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Chart from "chart.js";
import PieChart from "./PieChart.js";
import BarChart from "./BarChart.js";
import LineChart from "./LineChart.js";
import { mapState } from "vuex";
import PremiumStatCard from "../common/PremiumStatCard.vue";

export default {
  components: { PieChart, BarChart, LineChart, PremiumStatCard },

  data() {
    return {
      openComplaints: 0,
      visitors_today: 0,
      vehicles: 0,
      upcomingevents: 0,
      latestEvents: [],
      securityguards: 0,
      notices: 0,
      committeeMembers: 0,
      memberRequests: 0,
      members: 0,
      societyCode: "",
      unpaid_bills: 0,
      monthly_collection: "",
      paid_bills: 0,
      latestRequests: [],
      latestComplaints: [],
      latestNotices: [],
      latestExpenses: [],
      latestPayments: [],
      latestTickets: [],
      loaded: false,
      currentPlan: [],
      showDate: false,
      finLoading: false,
      rawFinData: null,
      occupancy: {
        total: 0,
        occupied: 0,
        owners: 0,
        tenants: 0,
        vacant: 0,
        pct: 0,
      },
      appAdoption: { total: 0, users: 0, pct: 0 },
      colHealth: { total: 0, collected: 0, pct: 0 },
      setupStatusLoading: false,
      scExpanded: false,
      setupStatus: {
        needs_blocks: false,
        has_blocks: false,
        has_units: false,
        hidden: false,
        all_complete: false,
        total_steps: 0,
        completed_steps: 0,
        steps: [],
      },
      setupBannerDismissed: false,

      /* QR Code */
      qrLoading: false,
      qrUrl: "",
      qrImageUrl: "",
      pdfUrl: "",

      /* Dynamic Chart Data */
      finChartData: null,
      finChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "top",
          labels: {
            usePointStyle: true,
            fontColor: "#9CA3AF",
            fontSize: 11,
            padding: 20,
          },
          onClick: function (e, legendItem) {
            var index = legendItem.datasetIndex;
            var ci = this.chart;
            var meta = ci.getDatasetMeta(index);
            meta.hidden =
              meta.hidden === null ? !ci.data.datasets[index].hidden : null;
            ci.update({ duration: 1000, easing: "easeOutQuart" });
          },
        },
        animation: {
          duration: 1500,
          easing: "easeOutQuart",
        },
        responsiveAnimationDuration: 1500,
        hover: { mode: "index", intersect: false, animationDuration: 600 },
        layout: { padding: { top: 20, bottom: 5, left: 5, right: 15 } },
        scales: {
          yAxes: [
            {
              stacked: true,
              gridLines: {
                display: true,
                borderDash: [6, 6],
                color: "rgba(0,0,0,0.1)",
                drawBorder: false,
                zeroLineColor: "transparent",
              },
              ticks: {
                beginAtZero: true,
                fontColor: "#9CA3AF",
                fontSize: 11,
                padding: 16,
                callback: (v) => {
                  if (v >= 100000) return "₹" + (v / 100000).toFixed(1) + "L";
                  if (v >= 1000) return "₹" + v / 1000 + "k";
                  return "₹" + v;
                },
              },
            },
          ],
          xAxes: [
            {
              stacked: true,
              gridLines: { display: false, drawBorder: false },
              ticks: { fontColor: "#9CA3AF", fontSize: 11, padding: 12 },
              categoryPercentage: 0.7,
              barPercentage: 0.9,
              offset: true,
            },
          ],
        },
        tooltips: {
          mode: "index",
          intersect: false,
          backgroundColor: "#ffffff",
          cornerRadius: 10,
          xPadding: 15,
          yPadding: 15,
          caretPadding: 12,
          titleFontSize: 13,
          titleFontColor: "#0F172A",
          titleFontFamily: "'Bricolage Grotesque', sans-serif",
          titleMarginBottom: 10,
          bodyFontSize: 12,
          bodyFontColor: "#334155",
          bodyFontFamily: "'Instrument Sans', sans-serif",
          bodySpacing: 8,
          footerFontColor: "#64748B",
          footerFontFamily: "'Instrument Sans', sans-serif",
          footerFontSize: 11,
          footerMarginTop: 10,
          borderColor: "rgba(0,0,0,0.06)",
          borderWidth: 1,
          displayColors: true,
          callbacks: {
            title: (items) => {
              return items[0].xLabel + " Overview";
            },
            labelColor: (tooltipItem, chart) => {
              const dataset =
                chart.config.data.datasets[tooltipItem.datasetIndex];
              return {
                borderColor:
                  dataset.hoverBackgroundColor || dataset.backgroundColor,
                backgroundColor:
                  dataset.hoverBackgroundColor || dataset.backgroundColor,
              };
            },
            label: (i, d) => {
              const label = d.datasets[i.datasetIndex].label;
              const val = Number(i.yLabel);
              return ` ${label}: ₹${val.toLocaleString()}`;
            },
            // footer: (tooltipItems) => {
            //   const idx = tooltipItems[0].index;
            //   if (this.rawFinData && this.rawFinData.collected && this.rawFinData.pending) {
            //     const c = Number(this.rawFinData.collected[idx]) || 0;
            //     const p = Number(this.rawFinData.pending[idx]) || 0;
            //     return `Total Bill: ₹${(c + p).toLocaleString()}`;
            //   }
            //   return '';
            // }
          },
        },
      },

      occChartData: null,
      occChartOptions: {
        cutoutPercentage: 78,
        tooltips: {
          backgroundColor: "#ffffff",
          cornerRadius: 10,
          xPadding: 12,
          yPadding: 12,
          caretPadding: 10,
          titleFontColor: "#0F172A",
          bodyFontColor: "#334155",
          borderColor: "rgba(0,0,0,0.06)",
          borderWidth: 1,
        },
      },

      growChartData: null,
      growChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "top",
          labels: {
            usePointStyle: true,
            fontColor: "#9CA3AF",
            fontSize: 11,
            padding: 25,
          },
          onClick: function (e, legendItem) {
            var index = legendItem.datasetIndex;
            var ci = this.chart;
            var meta = ci.getDatasetMeta(index);
            meta.hidden =
              meta.hidden === null ? !ci.data.datasets[index].hidden : null;
            ci.update({ duration: 1000, easing: "easeOutQuart" });
          },
        },
        animation: {
          duration: 1500,
          easing: "easeOutQuart",
        },
        responsiveAnimationDuration: 1500,
        hover: { mode: "index", intersect: false, animationDuration: 600 },
        layout: { padding: { top: 10, bottom: 5, left: 5, right: 15 } },
        scales: {
          yAxes: [
            {
              gridLines: {
                display: true,
                color: "rgba(0,0,0,0.1)",
                drawBorder: false,
                zeroLineColor: "transparent",
              },
              ticks: {
                beginAtZero: true,
                fontColor: "#9CA3AF",
                fontSize: 11,
                padding: 12,
                precision: 0,
                stepSize: 5,
              },
            },
          ],
          xAxes: [
            {
              gridLines: { display: false, drawBorder: false },
              ticks: { fontColor: "#9CA3AF", fontSize: 11, padding: 12 },
            },
          ],
        },
        tooltips: {
          mode: "index",
          intersect: false,
          backgroundColor: "#ffffff",
          cornerRadius: 10,
          xPadding: 15,
          yPadding: 15,
          caretPadding: 12,
          titleFontSize: 13,
          titleFontColor: "#0F172A",
          titleFontFamily: "'Bricolage Grotesque', sans-serif",
          titleMarginBottom: 10,
          bodyFontSize: 12,
          bodyFontColor: "#334155",
          bodyFontFamily: "'Instrument Sans', sans-serif",
          bodySpacing: 8,
          borderColor: "rgba(0,0,0,0.06)",
          borderWidth: 1,
          displayColors: true,
        },
      },

      adoptChartData: null,
      adoptChartOptions: {
        cutoutPercentage: 70,
        tooltips: {
          backgroundColor: "#ffffff",
          cornerRadius: 10,
          xPadding: 12,
          yPadding: 12,
          caretPadding: 10,
          titleFontColor: "#0F172A",
          bodyFontColor: "#334155",
          borderColor: "rgba(0,0,0,0.06)",
          borderWidth: 1,
        },
      },
    };
  },

  // computed: {
  //   ...mapState('theme', ['primaryColor', 'isDark']),
  // },

  watch: {
    isDark() {
      this.updateChartThemes();
    },
  },

  methods: {
    getCounts() {
      this.occChartData = null;
      this.growChartData = null;
      this.adoptChartData = null;
      this.$axios.get("societyadmin/dashboard").then((response) => {
        let data = response.data;
        // Map dynamic data if available
        if (data.openComplaints !== undefined)
          this.openComplaints = data.openComplaints;
        if (data.visitors_today !== undefined)
          this.visitors_today = data.visitors_today;
        if (data.vehicles !== undefined) this.vehicles = data.vehicles;
        if (data.upcoming_events_count !== undefined)
          this.upcomingevents = data.upcoming_events_count;
        if (data.events !== undefined) this.latestEvents = data.events;
        if (data.upcoming_events_count !== undefined)
          this.upcomingevents = data.upcoming_events_count;
        if (data.events !== undefined) this.latestEvents = data.events;
        if (data.member_requests_count !== undefined)
          this.memberRequests = data.member_requests_count;
        if (data.member_requests !== undefined)
          this.latestRequests = data.member_requests;
        if (data.help_desk_issues !== undefined)
          this.latestComplaints = data.help_desk_issues;
        if (data.complaints !== undefined) this.latestTickets = data.complaints;
        if (data.latest_notices !== undefined)
          this.latestNotices = data.latest_notices;
        if (data.latest_expenses !== undefined)
          this.latestExpenses = data.latest_expenses;
        if (data.latest_payments !== undefined)
          this.latestPayments = data.latest_payments;
        if (data.securityguards !== undefined)
          this.securityguards = data.securityguards;
        if (data.members !== undefined) this.members = data.members;
        if (data.unpaid_bills !== undefined)
          this.unpaid_bills = data.unpaid_bills;
        if (data.monthly_collection !== undefined)
          this.monthly_collection = data.monthly_collection;
        if (data.paid_bills !== undefined) this.paid_bills = data.paid_bills;
        if (data.society_code) this.societyCode = data.society_code;
        if (data.occupancy) {
          this.occupancy = data.occupancy;
          this.occChartData = {
            labels: ["Occupied", "Vacant"],
            datasets: [
              {
                data: [data.occupancy.occupied, data.occupancy.vacant],
                backgroundColor: ["#10B981", "#E5E7EB66"],
                borderWidth: 0,
                hoverBackgroundColor: ["#059669", "#E5E7EB"],
              },
            ],
          };
        }
        if (data.app_adoption) {
          this.appAdoption = data.app_adoption;
          this.adoptChartData = {
            labels: ["App Users", "Offline"],
            datasets: [
              {
                data: [
                  data.app_adoption.users,
                  data.app_adoption.total - data.app_adoption.users,
                ],
                backgroundColor: ["#3B82F6", "#E5E7EB66"],
                borderWidth: 0,
              },
            ],
          };
        }
        if (data.collection_health) {
          this.colHealth = data.collection_health;
        }

        // Fetch other dynamic chart data
        this.getFinanceOverview();
        this.getGrowthTrend();

        this.loaded = true;
        this.$nextTick(() => {
          this.updateChartThemes();
        });
      });
    },
    getStatusColor(status) {
      // Tickets/Complaints status colors
      if (status == 1) return "#EF4444"; // Open
      if (status == 2) return "#F59E0B"; // In Progress
      if (status == 3) return "#10B981"; // Resolved/Closed
      return "#64748B";
    },
    getSetupStatus() {
      this.setupStatusLoading = true;
      this.$axios
        .get("societyadmin/dashboard/setup-status")
        .then((response) => {
          const data = response.data;
          if (data.status) {
            const allComplete = !!data.all_complete;
            // If setup is not complete, ignore any previous dismiss
            if (!allComplete) {
              this.setupBannerDismissed = false;
              localStorage.removeItem(
                this._setupDismissKey || "dashboardSetupBannerDismissed",
              );
            }
            this.setupStatus = {
              needs_blocks: !!data.needs_blocks,
              has_blocks: !!data.has_blocks,
              has_units: !!data.has_units,
              hidden: allComplete,
              all_complete: allComplete,
              total_steps: data.total_steps || 0,
              completed_steps: data.completed_steps || 0,
              steps: data.steps || [],
            };
          }
        })
        .finally(() => {
          this.setupStatusLoading = false;
        });
    },
    navigateSetupAction() {
      // Navigate to first pending step
      const firstPending = this.setupStatus.steps.find((s) => !s.done);
      if (firstPending) {
        this.$router.push(firstPending.route);
      }
    },
    dismissSetupBanner() {
      this.setupStatus.hidden = true;
      this.setupBannerDismissed = true;
      localStorage.setItem(
        this._setupDismissKey || "dashboardSetupBannerDismissed",
        "1",
      );
    },

    /* ── QR Code Methods ── */
    loadQR() {
      this.qrLoading  = true;
      this.qrUrl      = "";
      this.qrImageUrl = "";
      this.pdfUrl     = "";
      this.$axios
        .get(`society/${this.societyId}/qr-code`)
        .then((response) => {
          if (response.data.status) {
            this.qrUrl      = response.data.qr_url;
            this.qrImageUrl = this.API_BASE_URL + "/" + response.data.qr_code_url;
            this.pdfUrl     = response.data.pdf_path
              ? this.API_BASE_URL + "/" + response.data.pdf_path
              : "";
          } else {
            this.showError("Failed to generate QR Code");
          }
        })
        .catch(() => {
          this.showError("Failed to generate QR Code");
        })
        .finally(() => {
          this.qrLoading = false;
        });
    },

    downloadPDF() {
      if (!this.pdfUrl) { this.showError("PDF not available yet"); return; }
      this.downloadFile(this.pdfUrl, "visitor_checkin_qr.pdf");
    },

    saveBlob(blob, fileName) {
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, fileName);
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },

    async downloadFile(url, name) {
      if (!url) return;
      if (url.startsWith("blob:")) {
        try {
          const res = await fetch(url);
          const blob = await res.blob();
          this.saveBlob(blob, name);
        } catch (e) {
          console.error("[Visitor QR] blob fetch failed:", e);
        }
        return;
      }
      try {
        const res = await fetch(url, { method: "GET", credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        this.saveBlob(blob, name);
        return;
      } catch (fetchErr) {
        console.warn("[Visitor QR] fetch failed, trying axios:", fetchErr);
      }
      try {
        const response = await this.$axios.get(url, { responseType: "blob" });
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: "application/octet-stream" });
        this.saveBlob(blob, name);
      } catch (axiosErr) {
        console.error("[Visitor QR] Download failed:", axiosErr);
        alert("Download failed. Please right-click and choose 'Save as…'");
      }
    },

    showError(msg) {
      this.$store && this.$store.commit
        ? this.$store.commit("alert/setAlert", { showAlert: true, message: msg, variant: "red accent-3", timeout: 3000 })
        : alert(msg);
    },
    formatDate(ts, fmt) {
      if (!ts) return "";
      // Handle both numeric strings/timestamps and ISO date strings
      const d = isNaN(ts) ? new Date(ts) : new Date(Number(ts));
      if (isNaN(d.getTime())) return "Invalid Date";

      if (fmt === "MMM")
        return d.toLocaleString("en-US", { month: "short" }).toUpperCase();
      if (fmt === "DD") return d.getDate();
      if (fmt === "MMM D")
        return d.toLocaleString("en-US", { month: "short", day: "numeric" });
      if (fmt === "h A")
        return d.toLocaleString("en-US", { hour: "numeric", hour12: true });
      return d.toLocaleDateString();
    },
    getFinanceOverview() {
      this.finLoading = true;
      this.$axios
        .get("societyadmin/dashboard/finance-overview")
        .then((response) => {
          const fd = response.data;
          this.rawFinData = fd;
          if (fd && fd.months) {
            const hasData = [...fd.bills, ...fd.expenses].some(
              (v) => parseFloat(v) > 0,
            );
            if (hasData) {
              // Dynamic Axes Logic - Now including pending for total height
              const maxVal = Math.max(
                ...fd.months.map(
                  (_, i) =>
                    (parseFloat(fd.collected[i]) || 0) +
                    (parseFloat(fd.pending[i]) || 0),
                ),
                ...fd.expenses.map((v) => parseFloat(v) || 0),
                10,
              );

              let stepSize = 1000;
              let suggestedMax = 5000;

              if (maxVal <= 10) {
                stepSize = 2;
                suggestedMax = 10;
              } else if (maxVal <= 100) {
                stepSize = 20;
                suggestedMax = 100;
              } else if (maxVal <= 1000) {
                stepSize = 200;
                suggestedMax = 1000;
              } else if (maxVal <= 10000) {
                stepSize = 2000;
                suggestedMax = 10000;
              } else if (maxVal <= 50000) {
                stepSize = 10000;
                suggestedMax = 50000;
              } else if (maxVal <= 100000) {
                stepSize = 20000;
                suggestedMax = 100000;
              } else if (maxVal <= 500000) {
                stepSize = 100000;
                suggestedMax = 500000;
              } else {
                stepSize = Math.ceil(maxVal / 5 / 10000) * 10000;
                suggestedMax = Math.ceil(maxVal / stepSize) * stepSize;
              }

              this.finChartOptions.scales.yAxes[0].ticks.stepSize = stepSize;
              this.finChartOptions.scales.yAxes[0].ticks.suggestedMax =
                suggestedMax;

              this.finChartData = {
                labels: fd.months,
                datasets: [
                  {
                    type: "bar",
                    label: "Collected",
                    stack: "bills",
                    data: fd.collected,
                    backgroundColor: "#10B981",
                    hoverBackgroundColor: "#059669",
                    barThickness: 50,
                    order: 2,
                  },
                  {
                    type: "bar",
                    label: "Pending",
                    stack: "bills",
                    data: fd.pending,
                    backgroundColor: "rgba(16, 185, 129, 0.75)",
                    hoverBackgroundColor: "rgba(16, 185, 129, 0.5)",
                    barThickness: 50,
                    order: 2,
                  },
                  {
                    type: "bar",
                    label: "Expenses",
                    stack: "expenses",
                    data: fd.expenses,
                    backgroundColor: "#F59E0B",
                    hoverBackgroundColor: "#D97706",
                    barThickness: 50,
                    order: 2,
                  },
                ],
              };
            } else {
              this.finChartData = null;
            }
          }
          this.finLoading = false;
        })
        .catch(() => {
          this.finLoading = false;
          this.finChartData = null;
        });
    },
    getUserPlan() {
      this.$axios.get("societyadmin/user-plan").then((response) => {
        let data = response.data;
        if (data.status) {
          this.currentPlan = data.current;
          this.showDate = data.showDate;
          if (!data.isExpired) {
            localStorage.removeItem("isPlanExpired");
          } else {
            localStorage.setItem("isPlanExpired", "true");
          }
        }
      });
    },
    getGrowthTrend() {
      this.$axios
        .get("societyadmin/dashboard/growth-trend")
        .then((response) => {
          const gd = response.data;
          if (gd && gd.labels) {
            this.growChartData = {
              labels: gd.labels,
              datasets: [
                {
                  label: "Members",
                  data: gd.members,
                  borderColor: "#3B82F6",
                  backgroundColor: "rgba(59, 130, 246, 0.08)",
                  fill: true,
                  tension: 0.4,
                  borderWidth: 3,
                  pointRadius: 4,
                  pointHoverRadius: 7,
                  pointBackgroundColor: "#ffffff",
                  pointHoverBackgroundColor: "#3B82F6",
                  pointBorderColor: "#3B82F6",
                  pointHoverBorderColor: "#ffffff",
                  pointHoverBorderWidth: 3,
                },
                {
                  label: "Visitors",
                  data: gd.visitors,
                  borderColor: "#10B981",
                  backgroundColor: "rgba(16, 185, 129, 0.08)",
                  fill: true,
                  tension: 0.4,
                  borderWidth: 3,
                  pointRadius: 4,
                  pointHoverRadius: 7,
                  pointBackgroundColor: "#ffffff",
                  pointHoverBackgroundColor: "#10B981",
                  pointBorderColor: "#10B981",
                  pointHoverBorderColor: "#ffffff",
                  pointHoverBorderWidth: 3,
                },
              ],
            };

            // Calculate max to determine steps
            const allData = [...gd.members, ...gd.visitors];
            const maxVal = Math.max(...allData, 1);
            let stepSize = 5;
            let suggestedMax = 30;

            if (maxVal <= 30) {
              stepSize = 5;
              suggestedMax = 30;
            } else if (maxVal <= 100) {
              stepSize = 10;
              suggestedMax = 100;
            } else {
              stepSize = Math.ceil(maxVal / 10 / 5) * 5;
              suggestedMax = Math.ceil(maxVal / stepSize) * stepSize;
            }

            // Ensure suggestedMax is at least the maxVal
            if (suggestedMax < maxVal)
              suggestedMax = Math.ceil(maxVal / stepSize) * stepSize;

            this.$set(
              this.growChartOptions.scales.yAxes[0].ticks,
              "stepSize",
              stepSize,
            );
            this.$set(
              this.growChartOptions.scales.yAxes[0].ticks,
              "max",
              suggestedMax,
            );
          }
        })
        .catch((err) => {
          console.error("Error fetching growth trend", err);
        });
    },
    getFormattedDate(date) {
      if (!date) return "";
      const d = new Date(date);
      const y = d.getFullYear();
      const m = (d.getMonth() + 1).toString().padStart(2, "0");
      const day = d.getDate().toString().padStart(2, "0");
      return `${day}-${m}-${y}`;
    },
    updateChartThemes() {
      // Optional: update themes dynamically when toggling dark mode. We forced false in theme store, keeping static is fine for now.
    },
    checkTruncation(e, item) {
      const el = e.target;
      this.$set(item, "isTruncated", el.scrollWidth > el.clientWidth);
    },

    /* ── Setup checklist helpers ── */
    stepIcon(key) {
      const icons = {
        profile: "mdi-domain",
        blocks: "mdi-office-building-outline",
        units: "mdi-home-city-outline",
        members: "mdi-account-group-outline",
        committee: "mdi-account-tie-outline",
        requests: "mdi-account-check-outline",
        access: "mdi-cellphone-check",
        bank: "mdi-bank-outline",
      };
      return icons[key] || "mdi-circle-outline";
    },
    stepDesc(key) {
      const descs = {
        profile: "Fill society name, address and contact details.",
        blocks: "Define building blocks (wings/towers) before adding units.",
        units: "Create house & shop units for your members.",
        members: "Add at least one approved member to the society.",
        committee: "Assign committee members to manage the society.",
        requests: "Review and approve pending member join requests.",
        access: "At least one member should log in via the app.",
        bank: "Add bank account or UPI ID to enable online payments.",
      };
      return descs[key] || "";
    },
    stepActionLabel(key) {
      const labels = {
        profile: "Edit Profile",
        blocks: "Add Block",
        units: "Add Unit",
        members: "Add Member",
        committee: "Add Committee",
        requests: "Review",
        access: "View Grant Member Access",
        bank: "Add Details",
      };
      return labels[key] || "Go";
    },
  },

  mounted() {
    // Use society-specific dismiss key so each society has its own banner state
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const societyId = user.id || user._id || "default";
    this._setupDismissKey = `dashboardSetupBannerDismissed_${societyId}`;
    this.setupBannerDismissed =
      localStorage.getItem(this._setupDismissKey) === "1";
    // Apply premium shadow to all Chart.js tooltips
    if (
      Chart &&
      Chart.Tooltip &&
      !Chart.Tooltip.prototype.draw.__antigravity_shadow
    ) {
      const originalDraw = Chart.Tooltip.prototype.draw;
      Chart.Tooltip.prototype.draw = function () {
        const ctx = this._chart.ctx;
        ctx.save();
        ctx.shadowColor = "rgba(0, 0, 0, 0.12)";
        ctx.shadowBlur = 12;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 6;
        originalDraw.apply(this, arguments);
        ctx.restore();
      };
      Chart.Tooltip.prototype.draw.__antigravity_shadow = true;
    }

    // Apply rounded corners to all Bar charts
    if (
      Chart &&
      Chart.elements &&
      Chart.elements.Rectangle &&
      !Chart.elements.Rectangle.prototype.draw.__antigravity_rounded
    ) {
      Chart.elements.Rectangle.prototype.draw = function () {
        const ctx = this._chart.ctx;
        const vm = this._view;
        const cornerRadius = 8;

        if (!vm || vm.x === undefined || vm.y === undefined) return;

        let left, right, top, bottom;
        if (!vm.horizontal) {
          left = vm.x - vm.width / 2;
          right = vm.x + vm.width / 2;
          top = vm.y;
          bottom = vm.base;
        } else {
          left = vm.base;
          right = vm.x;
          top = vm.y - vm.height / 2;
          bottom = vm.y + vm.height / 2;
        }

        ctx.beginPath();
        ctx.fillStyle = vm.backgroundColor;
        ctx.strokeStyle = vm.borderColor;
        ctx.lineWidth = vm.borderWidth || 0;

        const width = Math.abs(right - left);
        const height = Math.abs(bottom - top);

        // Refinement: Only round the absolute top segment of a stack to avoid gaps
        let isTop = true;
        const chart = this._chart;
        const datasetIndex = this._datasetIndex;
        const index = this._index;
        const currentStack = chart.data.datasets[datasetIndex].stack;

        // Check if there are any visible datasets with non-zero data ABOVE this one in the same stack
        for (let i = datasetIndex + 1; i < chart.data.datasets.length; i++) {
          if (
            chart.isDatasetVisible(i) &&
            chart.data.datasets[i].stack === currentStack
          ) {
            const val = chart.data.datasets[i].data[index];
            if (val !== 0 && val !== null && val !== undefined) {
              isTop = false;
              break;
            }
          }
        }

        const radius = isTop
          ? Math.min(cornerRadius, width / 2, height / 2)
          : 0;

        if (height > 0) {
          if (!vm.horizontal) {
            // Vertical - round top
            ctx.moveTo(left + radius, top);
            ctx.lineTo(right - radius, top);
            ctx.quadraticCurveTo(right, top, right, top + radius);
            ctx.lineTo(right, bottom);
            ctx.lineTo(left, bottom);
            ctx.lineTo(left, top + radius);
            ctx.quadraticCurveTo(left, top, left + radius, top);
          } else {
            // Horizontal - round right
            ctx.moveTo(left, top);
            ctx.lineTo(right - radius, top);
            ctx.quadraticCurveTo(right, top, right, top + radius);
            ctx.lineTo(right, bottom - radius);
            ctx.quadraticCurveTo(right, bottom, right - radius, bottom);
            ctx.lineTo(left, bottom);
          }
          ctx.fill();
          if (vm.borderWidth) ctx.stroke();
        }
      };
      Chart.elements.Rectangle.prototype.draw.__antigravity_rounded = true;
    }

    this.getCounts();
    this.getUserPlan();
    this.getSetupStatus();
    this.loadQR();
  },
  computed: {
    ...mapState("theme", ["primaryColor", "isDark", "buttonColor"]),

    brandColor() {
      return this.buttonColor || this.primaryColor || "#10B981";
    },

    // Hex → RGB helper for rgba()
    brandRgb() {
      const hex = this.brandColor.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `${r},${g},${b}`;
    },

    societyId() {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user.society_id || user.id || user._id;
    },

    API_BASE_URL() {
      return process.env.VUE_APP_API_URL;
    },

    setupBannerStyle() {
      return {
        background: `linear-gradient(135deg, rgba(${this.brandRgb}, 0.12), rgba(${this.brandRgb}, 0.06))`,
        borderLeft: `4px solid ${this.brandColor}`,
      };
    },

    stepActiveStyle() {
      return {
        borderColor: this.brandColor,
        boxShadow: `0 12px 30px rgba(${this.brandRgb}, 0.15)`,
      };
    },

    stepNumberStyle() {
      return {
        background: `rgba(${this.brandRgb}, 0.1)`,
        color: this.brandColor,
      };
    },

    setupActionBtnStyle() {
      return {
        background: this.brandColor,
        color: "#ffffff",
      };
    },
    showSetupBanner() {
      if (this.setupBannerDismissed) return false;
      if (this.setupStatus.hidden) return false;
      if (this.setupStatus.all_complete) return false;
      // If steps not yet loaded, use legacy fields
      if (!this.setupStatus.steps || this.setupStatus.steps.length === 0) {
        if (this.setupStatus.needs_blocks) {
          return !this.setupStatus.has_blocks || !this.setupStatus.has_units;
        }
        return !this.setupStatus.has_units;
      }
      return !this.setupStatus.all_complete;
    },

    setupTotalSteps() {
      return (
        this.setupStatus.total_steps || (this.setupStatus.needs_blocks ? 2 : 1)
      );
    },
    setupCompletedSteps() {
      return this.setupStatus.completed_steps || 0;
    },
    setupProgressPct() {
      if (!this.setupTotalSteps) return 0;
      return Math.round(
        (this.setupCompletedSteps / this.setupTotalSteps) * 100,
      );
    },

    setupHeroStyle() {
      return {
        background: `linear-gradient(145deg, ${this.brandColor}, ${this.brandColor}dd)`,
      };
    },
  },
};
</script>

<style scoped>
.h-100 {
  height: 100%;
}

.h-98 {
  height: 98%;
}

/* ── Layout ────────────────────────────── */
.dash-wrap {
  padding: 24px;
}

/* ══════════════════════════════════════════════
   COLLAPSIBLE SETUP CHECKLIST PANEL
══════════════════════════════════════════════ */
.sc-panel {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.07),
    0 1px 6px rgba(15, 23, 42, 0.04);
}

/* Collapsed bar — always visible, clickable */
.sc-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}
.sc-bar:hover {
  background: var(--surface2, rgba(15, 23, 42, 0.025));
}
.sc-bar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.sc-bar-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sc-bar-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.sc-bar-title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--tx);
  letter-spacing: -0.01em;
}
.sc-bar-sub {
  font-size: 11px;
  color: var(--tx4);
  font-weight: 500;
}
.sc-bar-progress {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.sc-bar-track {
  flex: 1;
  height: 5px;
  background: var(--border);
  border-radius: 99px;
  overflow: hidden;
  min-width: 60px;
}
.sc-bar-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 1.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.sc-bar-pct {
  font-size: 12px;
  font-weight: 700;
  min-width: 34px;
  text-align: right;
  flex-shrink: 0;
}
.sc-bar-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.sc-toggle-btn {
  transition: transform 0.3s ease !important;
}
.sc-close-btn {
  opacity: 0.6;
}
.sc-close-btn:hover {
  opacity: 1;
}

/* Expanded body */
.sc-body {
  border-top: 1px solid var(--border);
  background: var(--surface2, rgba(15, 23, 42, 0.015));
  padding: 18px 20px 14px;
}

/* Steps grid — 2 columns on desktop */
.sc-steps-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 14px;
}
@media (max-width: 700px) {
  .sc-steps-grid {
    grid-template-columns: 1fr;
  }
  .sc-bar-progress {
    display: none;
  }
}

/* Individual step row */
.sc-step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.sc-step-done {
  opacity: 0.6;
}
.sc-step-pending {
  border-color: transparent;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
}
.sc-step-pending:hover {
  border-color: var(--border);
  box-shadow: 0 3px 12px rgba(15, 23, 42, 0.08);
}

/* Step circle indicator */
.sc-step-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.25s, border-color 0.25s;
}
.sc-step-num {
  font-size: 11px;
  font-weight: 800;
}

/* Step body text */
.sc-step-body {
  flex: 1;
  min-width: 0;
}
.sc-step-label {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--tx);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  line-height: 1.3;
}
.sc-step-desc {
  font-size: 11px;
  color: var(--tx4);
  margin-top: 2px;
  line-height: 1.45;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Badges */
.sc-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 1.5px 7px;
  border-radius: 20px;
  margin-left: 5px;
  letter-spacing: 0.02em;
  background: rgba(15, 23, 42, 0.07);
  color: var(--tx3);
}
.sc-badge-done {
  background: rgba(16, 185, 129, 0.13);
  color: #10b981;
}
.sc-badge-warn {
  background: rgba(245, 158, 11, 0.13);
  color: #d97706;
}
.sc-badge-todo {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

/* Quick-action mini button */
.sc-action-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 11px !important;
  border-radius: 8px !important;
  letter-spacing: 0 !important;
  flex-shrink: 0;
}
.sc-check-done {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

/* Footer strip */
.sc-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px solid var(--border);
}
.sc-footer-note {
  font-size: 11px;
  color: var(--tx4);
  font-style: italic;
}
.sc-skip-btn {
  text-transform: none !important;
  color: var(--tx4) !important;
  font-size: 11px !important;
}

/* Expand / collapse transition */
.sc-expand-enter-active,
.sc-expand-leave-active {
  transition: max-height 0.36s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease;
  overflow: hidden;
  max-height: 600px;
}
.sc-expand-enter,
.sc-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* display: flex;
  flex-wrap: nowrap;
} */
.stats-col {
  flex: 0 0 20% !important;
  max-width: 20% !important;
  display: flex;
}

.stats-col ::v-deep .premium-stat-card {
  width: 100%;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

/* Panels */
.panel {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: rgba(0, 0, 0, 0.04) 0px 2px 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 400px;
  /* Fixed height for consistency */
}

.panel-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--surface2);
}

.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--tx);
  letter-spacing: -0.01em;
  margin-bottom: 2px;
}

.panel-sub {
  font-size: 11.5px;
  color: var(--tx3);
  font-weight: 400;
}

.panel-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.panel-body::-webkit-scrollbar {
  display: none;
}

.chart-container-scroll {
  width: 100%;
  height: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.chart-inner-wrap {
  min-width: 500px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 960px) {
  .chart-inner-wrap {
    min-width: 320px;
  }
}

@media (max-width: 600px) {
  .chart-inner-wrap {
    min-width: 260px;
  }
}

.chart-container-scroll::-webkit-scrollbar {
  display: none;
}

.chart-body {
  height: 300px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Grids */
.g3-a {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
}

.g3-b {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
}

.g3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.g4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

/* Responsive grids handled in style.css global rules */

/* Society Code row */
.dash-toprow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
}

.society-code-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.society-code-label {
  font-size: 12px;
  color: var(--tx3);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.society-code-val {
  font-size: 13px;
  font-weight: 700;
  color: var(--brand);
  font-family: "JetBrains Mono", monospace;
  background: var(--surface3);
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid var(--brand);
}

/* ── Responsive breakpoints ─────────────── */

/* Tablet (≤960px) */
@media (max-width: 960px) {
  .dash-wrap {
    padding: 16px;
  }

  .stats-row {
    flex-wrap: wrap;
  }

  .stats-col {
    flex: 0 0 50% !important;
    max-width: 50% !important;
  }

  .chart-body {
    height: 260px !important;
  }

  .panel-hdr {
    padding: 12px 16px;
  }

  .panel-body {
    padding: 14px 16px;
  }
}

/* Mobile (≤600px) */
@media (max-width: 600px) {
  .dash-wrap {
    padding: 12px;
  }

  .stats-col {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  .chart-body {
    height: 220px !important;
  }

  .dash-toprow {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .society-code-wrap {
    width: 100%;
    justify-content: flex-start;
  }

  .society-code-label {
    display: none;
  }

  .panel-hdr {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .q-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .q-btn {
    padding: 10px 10px;
    gap: 8px;
  }

  .q-icon {
    width: 32px;
    height: 32px;
  }

  .q-lbl {
    font-size: 11.5px;
  }

  .panel-title {
    font-size: 13px;
  }

  .panel-body {
    padding: 12px;
  }
}

/* Quick Actions */
.q-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

@media (max-width: 1264px) {
  .q-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}

.q-btn {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
}

.q-btn:hover {
  border-color: var(--brand);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.q-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.q-lbl {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx);
}

/* Mod Rows */
.mod-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px dashed var(--border);
}

.mod-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.mod-l {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--tx2);
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.dg {
  background: #10b981;
}

.da {
  background: #f59e0b;
}

.dr {
  background: #ef4444;
}

.mod-v {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx);
  font-feature-settings: "tnum";
  font-variant-numeric: tabular-nums;
}

/* Health Bars */
.hbar {
  margin-bottom: 14px;
}

.hbar-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.hbar-lbl {
  font-size: 12px;
  font-weight: 500;
  color: var(--tx2);
}

.hbar-pct {
  font-size: 11px;
  font-weight: 700;
}

.hbar-track {
  height: 6px;
  background: var(--surface2);
  border-radius: 3px;
  overflow: hidden;
}

.hbar-fill {
  height: 100%;
  border-radius: 3px;
}

.health-box {
  background: var(--surface3);
  padding: 12px;
  border-radius: 8px;
  border-left: 3px solid var(--brand);
}

.health-main {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx);
  margin-bottom: 2px;
}

.health-sub {
  font-size: 11px;
  color: var(--tx3);
}

/* Buttons & Tags */
.view-all {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--brand);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.view-all:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.t-green {
  background: #ecfdf5;
  color: #10b981;
}

.t-amber {
  background: #fffbeb;
  color: #f59e0b;
}

.t-red {
  background: #fef2f2;
  color: #ef4444;
}

/* Dark mode tag overrides */
.theme--dark .t-green {
  background: rgba(52, 211, 153, 0.1);
  color: #34d399;
}

.theme--dark .t-amber {
  background: rgba(253, 230, 138, 0.08);
  color: #fcd34d;
}

.theme--dark .t-red {
  background: rgba(252, 165, 165, 0.08);
  color: #fca5a5;
}

/* Mini Events */
.mini-ev {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 10px;
}

.mini-ev:last-child {
  margin-bottom: 0;
}

.mini-ev-date {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  line-height: 1.1;
}

.mini-ev-mo {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.9;
  margin-bottom: 2px;
}

.mini-ev-day {
  font-size: 16px;
  font-weight: 800;
  font-family: "Bricolage Grotesque", sans-serif;
}

.mini-ev-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx);
  margin-bottom: 3px;
}

.mini-ev-loc {
  font-size: 11.5px;
  color: var(--tx3);
}

.filter-btn:hover {
  border-color: var(--brand) !important;
  background: rgba(16, 185, 129, 0.04) !important;
}

.pt-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--tx, #111827);
}

.pt-dark .pt-title {
  color: #f1f5f9;
}

.expense-title-trim {
  font-weight: 600;
  color: var(--tx);
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

@media (max-width: 1600px) {
  .expense-title-trim {
    max-width: 140px;
  }
}

@media (max-width: 1400px) {
  .expense-title-trim {
    max-width: 220px;
  }
}

@media (max-width: 600px) {
  .expense-title-trim {
    max-width: 80px;
  }

  .mod-l {
    font-size: 11px;
    gap: 4px;
  }

  .mod-v {
    font-size: 11px;
  }

  .panel-title {
    font-size: 12px !important;
  }

  .view-all {
    font-size: 10px !important;
  }
}

.premium-tooltip {
  background: var(--brand) !important;
  color: #fff !important;
  border-radius: 10px !important;
  padding: 10px 16px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2) !important;
  opacity: 1 !important;
  max-width: 300px !important;
  white-space: normal !important;
  line-height: 1.5 !important;
  text-align: center !important;
}

.premium-tooltip-card {
  background: var(--brand, #30384d);
  color: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.premium-tooltip-scroll {
  max-height: 200px;
  overflow-y: auto;
  display: block;
  font-size: 13px;
  line-height: 1.6;
}

.premium-tooltip-scroll::-webkit-scrollbar {
  width: 5px;
}

.premium-tooltip-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.premium-tooltip-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 10px;
}

/* House Number Badge */
.house-number-badge {
  display: inline-flex;
  align-items: center;
  background: var(--surface2, #f1f5f9);
  padding: 6px;
  border-radius: 6px;
  border: 1px solid var(--border, #e2e8f0);
}

.flat-prefix {
  font-size: 11px;
  font-family: "JetBrains Mono", monospace;
}

.house-num {
  font-size: 11px;
  font-family: "JetBrains Mono", monospace;
}

@media (max-width: 600px) {
  .sc-bar {
    padding: 12px 14px;
    gap: 10px;
  }
  .sc-bar-progress {
    display: none;
  }
  .sc-bar-icon {
    width: 30px;
    height: 30px;
  }
  .sc-bar-title {
    font-size: 12.5px;
  }
  .sc-bar-sub {
    font-size: 10px;
  }

  .sc-body {
    padding: 10px 10px 8px;
  }

  .sc-steps-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  /* KEY FIX: wrap into two rows on mobile */
  .sc-step {
    flex-wrap: wrap;
    padding: 10px;
    gap: 8px;
    align-items: flex-start;
  }

  /* Icon stays top-left, fixed size */
  .sc-step-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  /* Body takes remaining width next to icon */
  .sc-step-body {
    flex: 1;
    min-width: 0;
  }

  /* Step label: allow wrapping */
  .sc-step-label {
    font-size: 12px;
    flex-wrap: wrap;
    white-space: normal;
    line-height: 1.4;
    gap: 3px;
  }

  /* Description: 2 lines max */
  .sc-step-desc {
    white-space: normal;
    font-size: 10.5px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-top: 3px;
  }

  /* Badge */
  .sc-badge {
    font-size: 9px;
    padding: 1px 5px;
  }

  /* Action button / check icon: push to new row, align right */
  .sc-action-btn,
  .sc-check-done {
    margin-left: auto; /* pushes to right */
    flex-shrink: 0;
  }

  /* Action button full width feel on mobile */
  .sc-action-btn {
    font-size: 10px !important;
    padding: 0 10px !important;
    min-width: unset !important;
    height: 26px !important;
  }

  /* Done step: check icon inline with body */
  .sc-step-done .sc-check-done {
    align-self: flex-start;
    margin-top: 2px;
  }

  /* Footer */
  .sc-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-top: 8px;
  }
  .sc-footer-note {
    font-size: 10px;
  }
}

/* ══════════════════════════════════════
   VISITOR SELF CHECK-IN — INLINE PANEL
══════════════════════════════════════ */
.vqr-inline-grid {
  display: grid;
  /* grid-template-columns: 1fr 220px; */
  gap: 24px;
  align-items: start;
}
.vqr-inline-steps {
  padding-right: 8px;
}
.vqr-inline-step {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
}
.vqr-inline-step-num {
  width: 24px; height: 24px; border-radius: 50%;
  font-size: 11px; font-weight: 700; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.vqr-inline-step-title { font-size: 13px; font-weight: 600; color: var(--tx); }
.vqr-inline-step-sub   { font-size: 11.5px; color: var(--tx4); margin-top: 2px; }

.vqr-inline-frame-col {
  display: flex;
  align-items: center;
  justify-content: center;
}
.vqr-inline-frame {
  position: relative;
  background: #fff;
  border-radius: 16px;
  border: 1px solid var(--border);
  padding: 14px;
  display: inline-block;
}
.vqr-inline-img {
  width: 192px;
  height: 192px;
  object-fit: contain;
  display: block;
}
.vqr-inline-empty {
  width: 192px; height: 192px;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 10px;
  color: var(--tx4);
  font-size: 12px;
  text-align: center;
  border: 2px dashed var(--border);
  border-radius: 12px;
}

/* Corner decorators (shared) */
.qr-corner {
  position: absolute; width: 18px; height: 18px;
  border-width: 3px; border-style: solid;
}
.qr-corner-tl { top:-2px; left:-2px;   border-right:none; border-bottom:none; border-top-left-radius:8px; }
.qr-corner-tr { top:-2px; right:-2px;  border-left:none;  border-bottom:none; border-top-right-radius:8px; }
.qr-corner-bl { bottom:-2px; left:-2px;  border-right:none; border-top:none; border-bottom-left-radius:8px; }
.qr-corner-br { bottom:-2px; right:-2px; border-left:none;  border-top:none; border-bottom-right-radius:8px; }

@media (max-width: 600px) {
  .vqr-inline-grid { grid-template-columns: 1fr; }
  .vqr-inline-frame-col { justify-content: flex-start; }
  .vqr-inline-img { width: 160px; height: 160px; }
  .vqr-inline-empty { width: 160px; height: 160px; }
}
</style>
